import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Modal, Form, Spinner, Alert, Badge,
  Row, Col, InputGroup, Card
} from 'react-bootstrap';
import {
  FaTrashAlt, FaInfoCircle, FaEdit, FaEye, FaEyeSlash,
  FaSearch, FaUsers, FaSync
} from 'react-icons/fa';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

// API Client
import { postalAreaAPI } from '../../api/apiClient';

// Styles
import '../../assets/css/Driver.css';
import { FaTruckFast } from 'react-icons/fa6';

const INITIAL_STATE = {
  data: [],
  loading: true,
  error: '',
};

// ============================================
// Helper Functions
// ============================================
const ensureArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object') {
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.results)) return data.results;
    return [data];
  }
  return [];
};

// ============================================
// Main Component
// ============================================
function PostalArea() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ============================================
  // State Management
  // ============================================
  const [state, setState] = useState(INITIAL_STATE);
  const [togglingStatus, setTogglingStatus] = useState({});

  const highlightedUserId = useMemo(
    () => location.state?.scrollToUser,
    [location.state]
  );

  // ============================================
  // Effects
  // ============================================
  useEffect(() => {
    fetchPostalAreas();
  }, []);

  // ============================================
  // API Calls
  // ============================================
  const fetchPostalAreas = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await postalAreaAPI.index();

      if (response.success) {
        // ✅ التأكد من أن البيانات هي مصفوفة
        const fetchedData = ensureArray(response.data.data || response.data);
        setData(fetchedData);
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_fetch_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_fetch_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error fetching data:', err);
      setData([]); // ✅ تعيين مصفوفة فارغة
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = useCallback(async (id) => {
    if (togglingStatus[id]) return;

    try {
      setTogglingStatus(prev => ({ ...prev, [id]: true }));

      const response = await postalAreaAPI.toggleStatus(id);
      if (response.success) {
        // ✅ تحديث البيانات محلياً
        const fetchedData = ensureArray(response.data.data || response.data);
        setData(fetchedData);

        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking.status_updated_successfully'),
          timer: 2000,
          showConfirmButton: false,
        });

        await fetchPostalAreas();
      } else {
        throw new Error(response.message || t('failed_to_update_status'));
      }
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: err.message || t('failed_to_update_status'),
        timer: 3000,
      });
    } finally {
      setTogglingStatus(prev => ({ ...prev, [id]: false }));
    }
  }, [togglingStatus, t]);

  // ============================================
  // Render Helpers
  // ============================================
  const renderStatusBadge = useCallback((status) => {
    const isActive = status === "1" || status === 1;
    return (
      <Badge bg={isActive ? 'success' : 'danger'}>
        {isActive ? t('active') : t('inactive')}
      </Badge>
    );
  }, [t]);

  const renderDimensions = useCallback((item) => {
    const { width, height, length } = item;
    const hasDimensions = width || height || length;
    return hasDimensions
      ? `${width || '-'} × ${height || '-'} × ${length || '-'} cm`
      : '-';
  }, []);

  // ============================================
  // Loading State
  // ============================================
  if (loading && data.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: '300px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // ============================================
  // Main Render
  // ============================================
  return (
    <div className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <h3 className="fw-bold mb-0">
          <FaTruckFast className="me-2" />
          {t('postal_areas')}
        </h3>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setState(prev => ({ ...prev, error: '' }))}>
          <Alert.Heading>{t('error')}</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {/* Table */}
      <div className="table-container shadow-sm rounded">
        <div className="table-responsive">
          <Table bordered hover className="custom-table mb-0 text-center align-middle">
            <thead className="table-dark">
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th>{t('name')}</th>
                <th>{t('description')}</th>
                <th>{t('dimensions')}</th>
                <th>{t('total_weight')}</th>
                <th>{t('amount')}</th>
                <th>{t('status')}</th>
                <th style={{ width: '120px' }}>{t('approved')}</th>
              </tr>
            </thead>
            <tbody>
              {!Array.isArray(data) || data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-4 text-muted">
                    {t('no_data_available')}
                  </td>
                </tr>
              ) : (
                data.map((item, index) => {
                  const isHighlighted = item.id === highlightedUserId;
                  return (
                    <tr
                      key={item.id || index}
                      id={`postal-${item.id || index}`}
                      className={isHighlighted ? 'user-highlight' : ''}
                    >
                      <td>{index + 1}</td>
                      <td className="fw-medium">{item.name || '-'}</td>
                      <td className="text-start">{item.description || '-'}</td>
                      <td>{renderDimensions(item)}</td>
                      <td>{item.weight ? `${item.weight} Kg` : '-'}</td>
                      <td>{item.amount ? `${item.amount} Paletten` : '-'}</td>
                      <td>{renderStatusBadge(item.is_active)}</td>
                      <td>
                        <div className="d-flex align-items-center justify-content-center">
                          <Form.Check
                            type="switch"
                            id={`approved-${item.id || index}`}
                            checked={item.is_active === "1" || item.is_active === 1}
                            onChange={() => toggleStatus(item.id)}
                            disabled={togglingStatus[item.id]}
                            className="custom-switch"
                          />
                          {togglingStatus[item.id] && (
                            <Spinner animation="border" size="sm" className="ms-2" />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default PostalArea;